import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DownloadDropdown,
  DmaMap,
  Dropdown,
  DropdownToggleType,
  IconToggleButton,
  DatePicker,
  BPMTable,
} from "../../Components";
import WidgetContainer from "../../Components/WidgetContainer";
import ChartContainer from "../../Components/ChartContainer";
import AutoSizer from "react-virtualized-auto-sizer";
import { Neutral300, ConvergentColorMap6 as GEO_MAP_COLORS } from "../../utils/colors";
import { capitalizeWords, currencyFormatter, numberFormatter } from "../../MMM/MMMUtils";
import "./GeographicAnalysis.scss";
import * as R from "ramda";
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted,
} from "../../BrandHealthMetrics/BrandHealthMetricsUtils";
import BestAndWorstLegend, { colorsWorst, colorsBest } from "./Components/BestAndWorstLegend";
import FilterCheckbox from "./Components/FilterCheckbox";
import { MdOutlineMap, MdOutlineTableRows } from "react-icons/md";
import { DateRange } from "../../utils/types";
import GradientLegend from "../../BrandHealthMetrics/GradientLegend";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";

const BEST_AND_WORST_COLORS = [...colorsBest, ...colorsWorst];

const REVERSED_GEO_MAP_COLORS = [...GEO_MAP_COLORS].reverse();

const INSIGHT_LABELS = {
  revenue: {
    tooltip: "Revenue",
    legend: "Revenue",
  },
  kpiVolume: {
    tooltip: "KPI Volume",
    legend: "KPI Volume",
  },
  roas: {
    tooltip: "ROAS",
    legend: "ROAS",
  },
  cpa: {
    tooltip: "Cost Per Acquisition",
    legend: "CPA",
  },
  bestandworstroas: {
    tooltip: "ROAS",
    legend: "ROAS",
  },
  bestandworstcpa: {
    tooltip: "Cost Per Acquisition",
    legend: "CPA",
  },
};

const DATA_FORMATTERS = {
  revenue: (value: number) => currencyFormatter.format(value, 2),
  kpiVolume: (value: number) => numberFormatter.format(value, 2),
  roas: (value: number) => numberFormatter.format(value, 2),
  cpa: (value: number) => currencyFormatter.format(value, 2),
  bestandworstroas: (value: number) => numberFormatter.format(value, 2),
  bestandworstcpa: (value: number) => currencyFormatter.format(value, 2),
};

const INSIGHT_OPTIONS = {
  revenue: [
    { value: "revenue", label: "Revenue" },
    { value: "roas", label: "ROAS" },
    { value: "bestandworstroas", label: "Best and Worst ROAS" },
  ],
  kpiVolume: [
    { value: "kpiVolume", label: "KPI Volume" },
    { value: "cpa", label: "Cost Per Acquisition" },
    { value: "bestandworstcpa", label: "Best and Worst Cost Per Acquisition" },
  ],
};

const legendStyle = {
  background:
    "linear-gradient(90deg, #e8e5ff 0%, #e8e5ff 16.67%, #b5a6ff 16.67%, #b5a6ff 33.33%, #977aff 33.33%, #977aff 50%, #8254ff 50%, #8254ff 66.67%, #6b2def 66.67%, #6b2def 83.33%, #5200ce 83.33%, #5200ce 100%)",
};

interface GeoRankMap {
  [geo: string]: {
    rank: number;
    dma: string;
    incrementalOutcome: number;
    spend: number;
    response: number;
  };
}

interface GeographicAnalysisProps {
  groupBy: string;
  outcomeVariable: "revenue" | "kpiVolume";
  attributionByGeoRows: any[];
  geoNames: { [dma: string]: string };
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
}

export const GeographicAnalysis: React.FC<GeographicAnalysisProps> = ({
  groupBy,
  outcomeVariable,
  attributionByGeoRows,
  geoNames,
  channelBreakoutMap,
}) => {
  const mapSvgRef = useRef<SVGSVGElement | null>(null);
  const [gradientRange, setGradientRange] = useState<{ maxValue: number; minValue: number }>();
  const [insightsType, setInsightsType] = useState<string>(outcomeVariable);
  const [chartTypeView, setChartTypeView] = useState<string>("showMap");

  const displayOutcome = insightsType === "revenue" || insightsType === "kpiVolume";
  const displayBestAndWorst =
    insightsType === "bestandworstroas" || insightsType === "bestandworstcpa";
  const displayCpa = insightsType === "bestandworstcpa" || insightsType === "cpa";

  const [dateRange, setDateRange] = useState<DateRange>({
    start: "2023-01-02",
    end: "2024-12-23",
  });

  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

  const getSegmentLabel = (breakout: any, groupBy: string) => {
    if (!breakout) {
      return "";
    }
    if (groupBy === "channel") {
      return capitalizeWords(breakout.channel);
    } else if (groupBy === "platform") {
      return capitalizeWords(
        `${breakout.channel}${breakout.platform ? `, ${breakout.platform}` : ""}`
      );
    }
    return "";
  };

  const { attributionByGeoData } = useMemo(() => {
    let attributionByGeoDataMap: {
      [geo: string]: {
        dma: string;
        fullName: string;
        incrementalOutcome: number;
        spend: number;
        response: number;
      };
    } = {};
    if (!R.isEmpty(attributionByGeoRows)) {
      attributionByGeoRows.forEach(row => {
        const dma = row.geo.split("_")[1];

        if (row.metric !== "mean" || !geoNames[dma]) {
          return;
        }

        const breakout = channelBreakoutMap[row.channel?.toLowerCase() || ""];
        const segmentLabel = getSegmentLabel(breakout, groupBy);

        // Filter out channel breakouts that are not selected
        if (
          insightsType !== "bestandworstroas" &&
          selectedSegments.length >= 0 &&
          !selectedSegments.includes(segmentLabel)
        ) {
          return;
        }

        const { incremental_outcome: incrementalOutcome, spend } = row;
        if (!attributionByGeoDataMap[dma]) {
          attributionByGeoDataMap[dma] = {
            dma,
            fullName: geoNames[dma],
            incrementalOutcome: 0, // used for both revenue and kpiVolume
            spend: 0,
            response: 0, // used for both roi and cost per acquisition
          };
        }

        attributionByGeoDataMap[dma].incrementalOutcome += incrementalOutcome;
        attributionByGeoDataMap[dma].spend += spend;
      });

      // Calculate ROI/CPA for each DMA.
      R.keys(attributionByGeoDataMap).forEach(dma => {
        const { incrementalOutcome, spend } = attributionByGeoDataMap[dma];
        let response = 0;
        if (displayOutcome) {
          response = incrementalOutcome / spend;
        } else {
          response = spend / incrementalOutcome;
        }
        attributionByGeoDataMap[dma].response = response;
      });
    }

    return {
      attributionByGeoData: attributionByGeoDataMap,
    };
  }, [
    attributionByGeoRows,
    channelBreakoutMap,
    displayOutcome,
    geoNames,
    groupBy,
    insightsType,
    selectedSegments,
  ]);

  useEffect(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData)) && !R.isEmpty(R.keys(geoNames))) {
      const insightValues: number[] = R.values(attributionByGeoData).map(obj =>
        displayOutcome ? obj.incrementalOutcome : obj.response
      );
      const minValue = Math.min(...insightValues);
      const maxValue = Math.max(...insightValues);
      setGradientRange({
        minValue: displayCpa ? maxValue : minValue,
        maxValue: displayCpa ? minValue : maxValue,
      });
    }
  }, [attributionByGeoData, displayCpa, displayOutcome, geoNames, insightsType]);

  const { worstTenRanked, bestTenRanked } = useMemo<{
    worstTenRanked: GeoRankMap;
    bestTenRanked: GeoRankMap;
  }>(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData))) {
      // Sort entries based on roas or cpa
      const entries = Object.entries(attributionByGeoData)
        .map(([geo, data]) => ({ geo, ...data }))
        .sort((a, b) =>
          insightsType === "bestandworstroas" ? b.response - a.response : a.response - b.response
        );

      const top10 = entries.slice(0, 10);
      const bottom10 = entries.slice(-10).reverse();

      const combinedTop = top10.map((item, index) => ({ ...item, rank: index + 1 }));
      const combinedBottom = bottom10.map((item, index) => ({ ...item, rank: index + 1 }));

      const topRanked = Object.fromEntries(combinedTop.map(({ geo, ...rest }) => [geo, rest]));
      const bottomRanked = Object.fromEntries(
        combinedBottom.map(({ geo, ...rest }) => [geo, rest])
      );

      return {
        worstTenRanked: bottomRanked,
        bestTenRanked: topRanked,
      };
    }

    return {
      worstTenRanked: {} as GeoRankMap,
      bestTenRanked: {} as GeoRankMap,
    };
  }, [attributionByGeoData, insightsType]);

  const thresholds = getThresholdBinsWithStdDevNonAdjusted(
    Object.values(attributionByGeoData).map(item =>
      displayOutcome ? item.incrementalOutcome : item.response
    )
  );

  const { renderColor, renderToolTip } = useMemo(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData)) && !R.isEmpty(R.keys(geoNames)) && gradientRange) {
      let topAndBottomRankedByGeo;
      if (displayBestAndWorst) {
        const entries = Object.entries(attributionByGeoData)
          .map(([geo, data]) => ({ geo, ...data }))
          .sort((a, b) =>
            insightsType === "bestandworstroas" ? b.response - a.response : a.response - b.response
          );

        const top10 = entries.slice(0, 10);
        const bottom10 = entries.slice(-10).reverse();

        const combined = [...top10, ...bottom10].map((item, index) => ({
          ...item,
          rank: index + 1,
        }));

        topAndBottomRankedByGeo = Object.fromEntries(
          combined.map(({ geo, ...rest }) => [geo, rest])
        );
      }

      const renderColor = (dma_code: string) => {
        const { minValue, maxValue } = gradientRange;
        const range = Math.abs(maxValue - minValue);

        // Get the insights value for the given DMA code
        const insightsValue: number = displayOutcome
          ? attributionByGeoData[dma_code]?.incrementalOutcome
          : displayBestAndWorst
          ? topAndBottomRankedByGeo[dma_code]?.response
          : attributionByGeoData[dma_code]?.response;

        // If no response value or if all values are the same, return the first color
        if (insightsValue === undefined || range === 0) {
          return Neutral300;
        }

        return displayBestAndWorst
          ? BEST_AND_WORST_COLORS[topAndBottomRankedByGeo[dma_code]?.rank - 1]
          : getColorForIndexedVolume(
              insightsValue,
              thresholds,
              displayCpa ? REVERSED_GEO_MAP_COLORS : GEO_MAP_COLORS
            );
      };

      const renderToolTip = (dma_code: string) => {
        if (
          !geoNames[dma_code] ||
          (displayBestAndWorst && !(dma_code in topAndBottomRankedByGeo))
        ) {
          // If the DMA code is not in geoNames or not in best and worst, hide tooltip
          return;
        }

        const insightsValue = displayOutcome
          ? attributionByGeoData[dma_code]?.incrementalOutcome
          : attributionByGeoData[dma_code]?.response;

        if (!insightsValue) {
          let fieldValue = "N/A";
          if (insightsType === "revenue") {
            fieldValue = "$0";
          }
          if (insightsType === "kpiVolume") {
            fieldValue = "0";
          }

          return (
            <div className="geoMapTooltip">
              <div className="geoMapTooltipHeader">{`${
                geoNames[dma_code] ?? "N/A"
              } (DMA Code: ${dma_code})`}</div>
              <div className="geoMapTooltipBody">
                <div className="fieldName">{INSIGHT_LABELS[insightsType].tooltip}</div>
                <div className="fieldValue">{fieldValue}</div>
              </div>
            </div>
          );
        }

        let formattedInsightsValue = DATA_FORMATTERS[insightsType](insightsValue);

        return (
          <div className="geoMapTooltip">
            <div className="geoMapTooltipHeader">{`${
              geoNames[dma_code] ?? "N/A"
            } (DMA Code: ${dma_code})`}</div>
            <div className="geoMapTooltipBody">
              <div className="fieldName">{INSIGHT_LABELS[insightsType].tooltip}</div>
              <div className="fieldValue">{formattedInsightsValue}</div>
            </div>
          </div>
        );
      };

      return {
        renderColor,
        renderToolTip,
      };
    } else {
      return {
        renderColor: () => Neutral300,
        renderToolTip: () => "No info for this DMA",
      };
    }
  }, [
    attributionByGeoData,
    displayBestAndWorst,
    displayCpa,
    displayOutcome,
    geoNames,
    gradientRange,
    insightsType,
    thresholds,
  ]);

  const filterOptions = useMemo(() => {
    const segments = Object.values(channelBreakoutMap).reduce<string[]>((acc, entry) => {
      const seg = getSegmentLabel(entry, groupBy);
      if (seg && !acc.includes(seg)) {
        acc.push(seg);
      }
      return acc;
    }, []);
    return segments;
  }, [channelBreakoutMap, groupBy]);

  // By default have all channel breakouts selected except for Linear TV
  useEffect(() => {
    if (filterOptions.length) {
      setSelectedSegments(filterOptions.filter(option => option !== "Linear TV"));
    }
  }, [groupBy, filterOptions]);

  const insightOptions = useMemo(() => {
    const options = INSIGHT_OPTIONS[outcomeVariable];
    if (chartTypeView === "showTable") {
      return options.filter(
        option => option.value !== "bestandworstroas" && option.value !== "bestandworstcpa"
      );
    }

    return options;
  }, [chartTypeView, outcomeVariable]);

  const tableHeaders = useMemo(() => {
    return [
      {
        name: "fullName",
        label: "DMA Full Name",
        flex: 1,
      },
      {
        name: "dma",
        label: "DMA Code",
        flex: 1,
      },
      {
        name: displayOutcome ? "incrementalOutcome" : "response",
        label: INSIGHT_LABELS[insightsType].tooltip,
        flex: 1,
      },
      {
        name: "spend",
        label: "Spend",
        flex: 1,
      },
    ];
  }, [displayOutcome, insightsType]);

  const pngDownload = useCallback(async () => {
    await downloadPNG(".geoMapContainer", `${insightsType}_by_DMA`);
  }, [insightsType]);

  const excelDownload = useCallback(() => {
    if (displayBestAndWorst) {
      const data = Object.values(bestTenRanked)
        .sort((a, b) => a.rank - b.rank)
        .concat(Object.values(worstTenRanked).sort((a, b) => a.rank - b.rank));
      exportToExcel(data, `${insightsType}_by_DMA`);
    } else {
      exportToExcel(Object.values(attributionByGeoData), `${insightsType}_by_DMA`);
    }
  }, [attributionByGeoData, bestTenRanked, displayBestAndWorst, insightsType, worstTenRanked]);

  const gradientLegendFormatLabel = (value: number) => {
    if (insightsType === "revenue" && value >= 1000000) {
      return currencyFormatter.format(value, 2);
    } else if (insightsType === "revenue") {
      return currencyFormatter.format(value, 0);
    } else if (insightsType === "kpiVolume" && value >= 1000000) {
      return numberFormatter.format(value, 2);
    } else if (insightsType === "kpiVolume") {
      return numberFormatter.format(value, 0);
    } else {
      return DATA_FORMATTERS[insightsType](value);
    }
  };

  return (
    <div className="geographicAnalysisWidget">
      <WidgetContainer
        collapsible
        header="Geographic Analysis"
        subHeader="Below is the breakout of performance in each geography included in the model."
      >
        <div className="geographicAnalysisContainer">
          <ChartContainer
            enableHoverDesign
            rightActions={
              <>
                <div className="geographyAnalysisRightActions">
                  <DatePicker
                    range={dateRange}
                    onChange={setDateRange}
                    light={true}
                    disabled={true}
                  ></DatePicker>
                  <IconToggleButton
                    options={[
                      { key: "showMap", icon: <MdOutlineMap />, label: "map view" },
                      { key: "showTable", icon: <MdOutlineTableRows />, label: "table view" },
                    ]}
                    size="sm"
                    selectedOption={chartTypeView}
                    onChange={selected => setChartTypeView(selected)}
                    disabled={displayBestAndWorst}
                  />
                  <DownloadDropdown size="sm" onClickOptions={[excelDownload, pngDownload]} />
                </div>
              </>
            }
            title={
              <>
                <Dropdown
                  type={DropdownToggleType.WIDGET_TITLE}
                  value={insightsType}
                  options={insightOptions}
                  onChange={selection => setInsightsType(selection)}
                />
                <div>by DMA</div>
              </>
            }
            viewRightActions={true}
          >
            {chartTypeView === "showMap" && (
              <div className="geoMapContainer">
                {!displayBestAndWorst && (
                  <div className="geoGradientBar">
                    <GradientLegend
                      bins={thresholds}
                      useConvergent={false}
                      legendStyle={legendStyle}
                      formatLabel={gradientLegendFormatLabel}
                      reverse={displayCpa}
                    />
                  </div>
                )}
                <div className="geoMapAndFilter">
                  <div className="geoMap">
                    <AutoSizer>
                      {({ width, height }) => (
                        <DmaMap
                          width={width}
                          height={height}
                          renderColor={renderColor}
                          renderTooltip={renderToolTip}
                          ref={mapSvgRef}
                          borderColor="ffffff"
                        />
                      )}
                    </AutoSizer>
                  </div>
                  {!displayBestAndWorst && (
                    <div className="geoFilter">
                      <FilterCheckbox
                        filterOptions={filterOptions}
                        segments={filterOptions}
                        selectedSegments={selectedSegments}
                        setSelectedSegments={setSelectedSegments}
                      />
                    </div>
                  )}
                  {displayBestAndWorst && (
                    <div className="legendRow">
                      <BestAndWorstLegend
                        label={INSIGHT_LABELS[insightsType].legend}
                        data={bestTenRanked}
                        dataFormatter={DATA_FORMATTERS[insightsType]}
                        geoNames={geoNames}
                        isBest={true}
                      />
                      <BestAndWorstLegend
                        label={INSIGHT_LABELS[insightsType].legend}
                        data={worstTenRanked}
                        dataFormatter={DATA_FORMATTERS[insightsType]}
                        geoNames={geoNames}
                        isBest={false}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {chartTypeView === "showTable" && (
              <div className="geoTable">
                <BPMTable
                  data={Object.values(attributionByGeoData).map(item => {
                    return {
                      dma: item.dma,
                      fullName: item.fullName,
                      incrementalOutcome: DATA_FORMATTERS[insightsType](
                        item.incrementalOutcome
                      ) as string,
                      spend: currencyFormatter.format(item.spend, 2),
                      response: DATA_FORMATTERS[insightsType](item.response) as string,
                    };
                  })}
                  headers={tableHeaders}
                  filterBar={false}
                ></BPMTable>
              </div>
            )}
          </ChartContainer>
        </div>
      </WidgetContainer>
    </div>
  );
};

export default GeographicAnalysis;
